import React, { useState } from 'react';
import classNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import ZpButton from 'components/zpButton';
import { useCtxHistory } from 'hooks';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { surveyService } from 'services';
import { openSignUpModal } from 'store/modals/actions';
import { importStatic } from 'utils';

import classes from './index.module.css';

const AutosuggestInput = dynamic(() => import('components/autosuggestInput'), { ssr: false });

export interface SalaryCalculatorProps {
  title?: string;
  jobTitlePrefillValue?: string;
  locationPrefillValue?: string;
  calloutLocation?: string;
  TitleTagName?: keyof JSX.IntrinsicElements;
  dismissible?: boolean;
  titlePrefillValue?: string;
  src?: string;
}

const questions = [1, 2];

const SalaryCalculator = ({
  title,
  titlePrefillValue,
  locationPrefillValue,
  calloutLocation,
  TitleTagName,
  dismissible,
  src,
}: SalaryCalculatorProps) => {
  const history = useCtxHistory();
  const [error, setError] = useState(null);

  const dispatch = useAppDispatch();

  const isUserLoggedIn = useAppSelector(({ user }) => user && user.isLoggedIn);

  const [surveyResponse, setSurveyResponse] = useState<any>({});

  function createJobTitle(jobTitle = '') {
    return { normalizedTitle: jobTitle, originalSearch: jobTitle };
  }

  const [surveyData, setSurveyData] = useState<{
    [key: string]: {
      step: number;
      answer: any;
      normalizedTitle?: string;
      originalSearch?: string
    };
  }>({
    jobTitle: {
      step: 1,
      answer: titlePrefillValue ? createJobTitle(titlePrefillValue) : '',
    },
    location: {
      step: 2,
      answer: locationPrefillValue || '',
    },
  });

  const [showCalculator, setShowCalculator] = useState(true);

  const sendSurveyResponse = async (response = surveyResponse) => {
    const { questionList, surveyFlow, survey, utm_campaign, surveyStartTimeStamp, surveyId } =
      response;

    const surveyResponses = questions.map((q) => {
      let answer;
      const data = Object.values(surveyData).find((data) => data.step === q);
      if (data) {
        answer = data.answer ? [data.answer] : [];
      } else {
        answer = [];
      }
      return {
        questionId: questionList[q - 1]?.question?._id,
        surveyFlow: surveyFlow || 'surveyPopup',
        surveyName: survey,
        surveyId,
        surveyStep: q.toString(),
        utm_campaign,
        surveyStartTimeStamp,
        timeStamp: Date.now(),
        answer,
      };
    });

    for (let i = 0; i < surveyResponses.length; i++) {
      const res = surveyResponses[i];
      await surveyService.sendSurveyResponse(res);
    }
  };

  const calculateSalary = () => {
    const hasJobTitle =
      surveyData?.jobTitle?.answer?.normalizedTitle || surveyData?.jobTitle?.answer?.originalSearch;
    const hasLocation = surveyData?.location?.answer;

    if (!hasLocation) {
      setError('Please enter a location.');
      return null;
    }

    if (!hasJobTitle) {
      setError('Please enter a job title.');
      return null;
    }

    let query = {
      survey_step: 'step3',
      survey: '201',
      src: src ? src : 'jobtitle-salary-mini-hero',
      skipQuestion: isUserLoggedIn ? [...questions, 7] : questions,
      callout_location: undefined,
    };
    if (calloutLocation && calloutLocation.length) {
      query.callout_location = calloutLocation;
    }
    setError(null);
    dispatch(
      openSignUpModal(
        {
          ...query,
        },
        true
      )
    )
      .then((response) => {
        history.pushQuery(query);
        if (_isEmpty(surveyResponse)) {
          setSurveyResponse(response);
        }
        sendSurveyResponse(response);
      })
      .catch((err) => console.error(err));
  };

  const onAutoSuggestChange =
    (category) =>
    ({ displayValue, isListOpen }) => {
      if (isListOpen === false) {
        setSurveyData({
          ...surveyData,
          [category]: {
            ...surveyData[category],
            answer: category === 'jobTitle' ? createJobTitle(displayValue) : displayValue,
          },
        });
      }
    };

  if (!showCalculator) return null;

  return (
    <div className={classNames('d-flex bg-white border rounded', classes['salary-calculator'])}>
      <div className="z-p-20">
        {/* Header */}
        <div className="d-flex justify-content-between salary-calculator-title z-mb-16">
          <TitleTagName className="open-sans-large-black-bold mb-0">{title}</TitleTagName>
          {dismissible && (
            <Image
              className="close-icon"
              width="16"
              height="16"
              src={importStatic('/ui-router/images/resume-builder/close-modal.svg')}
              alt="Close Icon"
              onClick={() => setShowCalculator(false)}
            />
          )}
        </div>

        {/* Body */}
        <div className="d-flex flex-column">
          <AutosuggestInput
            category="location"
            prefillValue={locationPrefillValue}
            inputProps={{
              className: 'calculator-input-field',
              placeholder: 'Your Location*',
            }}
            onChange={onAutoSuggestChange('location')}
          />
          <AutosuggestInput
            category="jobTitle"
            prefillValue={titlePrefillValue}
            inputProps={{
              className: 'mt-2 calculator-input-field',
              placeholder: 'Your Job Title*',
            }}
            onChange={onAutoSuggestChange('jobTitle')}
          />
          {error && <p className="error-message mb-0 mt-3">{error}</p>}
          <ZpButton
            id="salary-report-callout"
            data-slug={src ? src : 'jobtitle-salary-mini-hero'}
            theme="primary"
            size="big"
            className="btn primary-button-big w-100"
            onClick={calculateSalary}
            isFullWidth
          >
            Get Your Estimate
          </ZpButton>
        </div>
      </div>

      <figure className="overflow-hidden my-auto">
        <img
          src={importStatic('/ui-router/images/half-calculator.svg')}
          alt="salary-calculator"
          className="salary-calculator-image"
        />
      </figure>
    </div>
  );
};

SalaryCalculator.defaultProps = {
  title: 'What Am I Worth?',
  jobTitlePrefillValue: '',
  locationPrefillValue: '',
  calloutLocation: '',
  TitleTagName: 'p',
  dismissible: true,
  titlePrefillValue: '',
};

export { SalaryCalculator };

export default SalaryCalculator;
